import React, { createContext, useContext, useState, useCallback, startTransition } from 'react';
import { createSlice } from '@reduxjs/toolkit';

// Mock data for demonstration
const integrationsState = {
  activeConnections: [
    {
      id: 'qb-main',
      type: 'quickbooks',
      status: 'connected',
      lastSync: '2024-11-08T10:30:00Z',
      entityName: 'Dunport Capital',
      settings: {
        syncFrequency: 'daily',
        autoImport: true,
        mappedAccounts: 45
      }
    },
    {
      id: 'xero-backup',
      type: 'xero',
      status: 'connected', 
      lastSync: '2024-11-08T09:15:00Z',
      entityName: 'TechFlow Solutions',
      settings: {
        syncFrequency: 'weekly',
        autoImport: false,
        mappedAccounts: 12
      }
    }
  ],
  pendingInvitations: [
    {
      id: 'inv-001',
      companyName: 'Sunset Crèche',
      email: 'finance@sunset-creche.co.uk',
      dateSent: '2024-11-05',
      status: 'pending',
      remindersSent: 1
    }
  ],
  recentActivity: [
    {
      id: 'activity-1',
      type: 'data_sync',
      message: 'QuickBooks sync completed - 156 transactions imported',
      timestamp: '2024-11-08T10:30:00Z',
      status: 'success'
    },
    {
      id: 'activity-2', 
      type: 'csv_import',
      message: 'CSV import completed for TechFlow Solutions - 23 records processed',
      timestamp: '2024-11-08T08:45:00Z',
      status: 'success'
    },
    {
      id: 'activity-3',
      type: 'error',
      message: 'Connection timeout with Xero API - retrying in 15 minutes',
      timestamp: '2024-11-07T16:22:00Z',
      status: 'warning'
    }
  ],
  // Add reputation bindings mock data
  reputationBindings: [
    {
      id: 'binding-1',
      companyId: 'comp-001',
      locationId: null,
      provider: 'google',
      status: 'active',
      locationName: 'Sunrise Crèche - Dublin',
      accountId: 'google-acc-1',
      createdAt: '2024-11-01T10:00:00Z'
    },
    {
      id: 'binding-2',
      companyId: 'comp-001',
      locationId: null,
      provider: 'facebook',
      status: 'active',
      locationName: 'Sunrise Crèche - Cork',
      accountId: 'facebook-acc-1',
      createdAt: '2024-11-02T14:30:00Z'
    }
  ]
};

// Redux slice for integrations
const integrationsSlice = createSlice({
  name: 'integrations',
  initialState: integrationsState,
  reducers: {
    updateIntegrationsState: (state, action) => {
      return { ...state, ...action?.payload };
    },
    addConnection: (state, action) => {
      state?.activeConnections?.push(action?.payload);
    },
    updateConnection: (state, action) => {
      const { id, updates } = action?.payload;
      const connectionIndex = state?.activeConnections?.findIndex(conn => conn?.id === id);
      if (connectionIndex !== -1) {
        state.activeConnections[connectionIndex] = { ...state?.activeConnections?.[connectionIndex], ...updates };
      }
    },
    removeConnection: (state, action) => {
      state.activeConnections = state?.activeConnections?.filter(conn => conn?.id !== action?.payload);
    },
    addActivity: (state, action) => {
      state.recentActivity = [action?.payload, ...state?.recentActivity]?.slice(0, 10);
    }
  }
});

// Export actions and reducer
export const { 
  updateIntegrationsState, 
  addConnection, 
  updateConnection, 
  removeConnection, 
  addActivity 
} = integrationsSlice?.actions;

// Export reducer as default for Redux store
export default integrationsSlice?.reducer;

// Context for React components (optional fallback)
const CapitalIntegrationsContext = createContext(undefined);

// Enhanced integrations state management with startTransition to prevent suspension errors
export function IntegrationsProvider({ children }) {
  const [state, setState] = useState(integrationsState);

  // Use startTransition for ALL state updates to prevent concurrent mode suspension
  const updateIntegrationsStateLocal = useCallback((updates) => {
    startTransition(() => {
      setState(prevState => ({ ...prevState, ...updates }));
    });
  }, []);

  // Wrap all potential state-changing operations in startTransition
  const addConnectionLocal = useCallback((connection) => {
    startTransition(() => {
      setState(prevState => ({
        ...prevState,
        activeConnections: [...(prevState?.activeConnections || []), connection]
      }));
    });
  }, []);

  const updateConnectionLocal = useCallback((id, updates) => {
    startTransition(() => {
      setState(prevState => ({
        ...prevState,
        activeConnections: (prevState?.activeConnections || [])?.map(conn =>
          conn?.id === id ? { ...conn, ...updates } : conn
        )
      }));
    });
  }, []);

  const removeConnectionLocal = useCallback((id) => {
    startTransition(() => {
      setState(prevState => ({
        ...prevState,
        activeConnections: (prevState?.activeConnections || [])?.filter(conn => conn?.id !== id)
      }));
    });
  }, []);

  const addActivityLocal = useCallback((activity) => {
    startTransition(() => {
      setState(prevState => ({
        ...prevState,
        recentActivity: [activity, ...(prevState?.recentActivity || [])]?.slice(0, 10)
      }));
    });
  }, []);

  const contextValue = {
    ...state,
    updateIntegrationsState: updateIntegrationsStateLocal,
    addConnection: addConnectionLocal,
    updateConnection: updateConnectionLocal,
    removeConnection: removeConnectionLocal,
    addActivity: addActivityLocal
  };

  return (
    <CapitalIntegrationsContext.Provider value={contextValue}>
      {children}
    </CapitalIntegrationsContext.Provider>
  );
}

// Hook
export const useCapitalIntegrations = () => {
  const context = useContext(CapitalIntegrationsContext);
  if (context === undefined) {
    throw new Error('useCapitalIntegrations must be used within a CapitalIntegrationsProvider');
  }
  return context;
};

// Types
export const INTEGRATION_PROVIDERS = {
  GOOGLE: 'google',
  FACEBOOK: 'facebook', 
  YELP: 'yelp',
  TRUSTPILOT: 'trustpilot',
  TRIPADVISOR: 'tripadvisor',
  TWITTER: 'twitter',
  REDDIT: 'reddit'
};

// Integration Account Status
export const ACCOUNT_STATUS = {
  CONNECTED: 'connected',
  EXPIRED: 'expired',
  ERROR: 'error'
};

// Integration Binding Status
export const BINDING_STATUS = {
  ACTIVE: 'active',
  ERROR: 'error',
  PENDING: 'pending'
};

// Binding Scope
export const BINDING_SCOPE = {
  TENANT: 'tenant',
  COMPANY: 'company', 
  LOCATION: 'location'
};

// Enhanced useIntegrations hook with proper error handling
export const useIntegrations = (...args) => {
  console.warn('Placeholder: useIntegrations is not implemented yet.', args);
  
  // Mock accounts data
  const mockAccounts = [
    {
      id: 'google_business_account_demo',
      provider: 'google',
      status: 'connected',
      accountName: 'Google Business Profile',
      email: 'business@example.com',
      createdAt: '2024-01-01T00:00:00Z'
    },
    {
      id: 'facebook_pages_account_demo',
      provider: 'facebook',
      status: 'connected',
      accountName: 'Facebook Pages',
      email: 'social@example.com',
      createdAt: '2024-01-01T00:00:00Z'
    }
  ];

  // Add getAccountsByProvider function
  const getAccountsByProvider = useCallback((provider) => {
    if (!provider) {
      return [];
    }
    
    return mockAccounts?.filter(acc => acc?.provider === provider) || [];
  }, []);

  // Add getBindingsByProvider function
  const getBindingsByProvider = useCallback((provider) => {
    if (!provider) {
      return [];
    }
    
    return integrationsState?.reputationBindings?.filter(
      binding => binding?.provider === provider
    ) || [];
  }, []);

  // Add addAccount function
  const addAccount = useCallback((account) => {
    console.log('Adding account:', account);
    // In production, this would update the actual state
    return account;
  }, []);

  // Add addBinding function
  const addBinding = useCallback((binding) => {
    console.log('Adding binding:', binding);
    // In production, this would update the actual state
    return binding;
  }, []);

  // Add removeAccount function
  const removeAccount = useCallback((accountId) => {
    console.log('Removing account:', accountId);
    // In production, this would update the actual state
    return accountId;
  }, []);

  // Add removeBinding function
  const removeBinding = useCallback((bindingId) => {
    console.log('Removing binding:', bindingId);
    // In production, this would update the actual state
    return bindingId;
  }, []);
  
  // Add getBindingsByCompany function
  const getBindingsByCompany = useCallback((companyId) => {
    if (!companyId) {
      return [];
    }
    
    // Return mock bindings for the company
    // In production, this would fetch from actual state/API
    return integrationsState?.reputationBindings?.filter(
      binding => binding?.companyId === companyId
    ) || [];
  }, []);

  // Add getCoverage function
  const getCoverage = useCallback((provider) => {
    if (!provider) {
      return { connected: 0, total: 0, percentage: 0 };
    }
    
    const bindings = integrationsState?.reputationBindings?.filter(
      binding => binding?.provider === provider && binding?.status === 'active'
    ) || [];
    
    // Mock total sources count
    const totalSources = 35;
    const connectedSources = bindings?.length;
    
    return {
      connected: connectedSources,
      total: totalSources,
      percentage: Math.round((connectedSources / totalSources) * 100)
    };
  }, []);

  // Add getBindingSummary function
  const getBindingSummary = useCallback(() => {
    const bindings = integrationsState?.reputationBindings || [];
    const activeBindings = bindings?.filter(b => b?.status === 'active');
    
    return {
      total: bindings?.length,
      active: activeBindings?.length,
      pending: bindings?.filter(b => b?.status === 'pending')?.length,
      error: bindings?.filter(b => b?.status === 'error')?.length
    };
  }, []);

  // Add getConnectedProviders function
  const getConnectedProviders = useCallback(() => {
    const bindings = integrationsState?.reputationBindings || [];
    const uniqueProviders = [...new Set(bindings?.map(b => b?.provider))];
    return uniqueProviders?.filter(provider => {
      const providerBindings = bindings?.filter(b => b?.provider === provider);
      return providerBindings?.some(b => b?.status === 'active');
    });
  }, []);

  return {
    accounts: mockAccounts,
    bindings: integrationsState?.reputationBindings || [],
    integrations: [],
    loading: false,
    error: null,
    fetchIntegrations: () => Promise.resolve([]),
    createIntegration: () => Promise.resolve(null),
    updateIntegration: () => Promise.resolve(null),
    deleteIntegration: () => Promise.resolve(null),
    getAccountsByProvider,
    getBindingsByProvider,
    addAccount,
    addBinding,
    removeAccount,
    removeBinding,
    getBindingsByCompany,
    getCoverage,
    getBindingSummary,
    getConnectedProviders,
    reputationBindings: integrationsState?.reputationBindings || []
  };
};